/**
 * PATH: packages/email-hub/src/feature/lib/stampPdfSignatures.ts
 * FILE_ID: EMAILHUB-LIB-STAMP-SIGNATURES-001
 * PURPOSE: Stamp signature images and dates onto chosen PDF pages.
 */
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib'
// Reused from Emails Hub: _pdfHelpers.ts (formatSignatureDate, same "05 June 2026" date as PdfEditor Cmd+D)
import { formatSignatureDate } from '../components/_pdfHelpers'

export interface PdfSignatureStamp {
  pngBytes: Uint8Array
  pages: number[]
  signedAt: Date
}

// Bottom-right corner, like PdfEditor bottomRightPlacement; sits inside the 32mm ITP signing band.
export const SIGNATURE_STAMP = {
  widthPt: 140,
  maxHeightPt: 48,
  rightPt: 34,
  bottomPt: 24,
  gapPt: 12,
  dateSizePt: 7,
} as const

/** Cap width, then shrink width if the height cap would flatten a tall mark. */
export function signatureFootprint(
  imageWidth: number,
  imageHeight: number,
  capWidth = 150,
  maxHeight = 90,
): { widthPt: number; heightPt: number } {
  if (imageWidth <= 0 || imageHeight <= 0) return { widthPt: capWidth, heightPt: 16 }
  let widthPt = capWidth
  let heightPt = Math.round(widthPt * (imageHeight / imageWidth))
  if (heightPt > maxHeight) {
    heightPt = maxHeight
    widthPt = Math.max(16, Math.round(maxHeight * (imageWidth / imageHeight)))
  }
  if (heightPt < 16) heightPt = 16
  return { widthPt, heightPt }
}

export function inkBounds(
  width: number,
  height: number,
  rgba: Uint8ClampedArray,
): { x: number; y: number; width: number; height: number } | null {
  let minX = width
  let minY = height
  let maxX = -1
  let maxY = -1
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (rgba[(y * width + x) * 4 + 3] < 16) continue
      if (x < minX) minX = x
      if (y < minY) minY = y
      if (x > maxX) maxX = x
      if (y > maxY) maxY = y
    }
  }
  if (maxX < 0) return null
  return { x: minX, y: minY, width: maxX - minX + 1, height: maxY - minY + 1 }
}

export async function countPdfPages(bytes: ArrayBuffer | Uint8Array): Promise<number> {
  return (await PDFDocument.load(bytes)).getPageCount()
}

export async function stampPdfSignatures(
  bytes: ArrayBuffer | Uint8Array,
  stamps: readonly PdfSignatureStamp[],
): Promise<Uint8Array> {
  const pdf = await PDFDocument.load(bytes)
  const font = await pdf.embedFont(StandardFonts.Helvetica)
  const pages = pdf.getPages()
  const slots = new Map<number, number>()
  const S = SIGNATURE_STAMP
  for (const stamp of stamps) {
    const image = await pdf.embedPng(stamp.pngBytes)
    const scale = Math.min(S.widthPt / image.width, S.maxHeightPt / image.height)
    const w = image.width * scale
    const h = image.height * scale
    const date = formatSignatureDate(stamp.signedAt)
    const dateW = font.widthOfTextAtSize(date, S.dateSizePt)
    for (const pageNumber of stamp.pages) {
      const page = pages[pageNumber - 1]
      if (!page) continue
      const slot = slots.get(pageNumber) ?? 0
      slots.set(pageNumber, slot + 1)
      // ponytail: slots march left; more than 3 on one page clamp at the left inset and can overlap.
      const x = Math.max(S.rightPt, page.getWidth() - S.rightPt - w - slot * (S.widthPt + S.gapPt))
      page.drawImage(image, { x, y: S.bottomPt + S.dateSizePt + 3, width: w, height: h })
      page.drawText(date, {
        x: x + (w - dateW) / 2,
        y: S.bottomPt,
        size: S.dateSizePt,
        font,
        color: rgb(0.27, 0.27, 0.27),
      })
    }
  }
  return pdf.save()
}
